import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./PackageSets.css";

const BASE = "https://backend.washta.com/api";
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("accessTokenAdmin")}` });

// ── helpers ──────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = { true: "green", false: "gray" };
  const label = status ? "Active" : "Inactive";
  return <span className={`ps-badge ${map[String(status)] || "gray"}`}>{label}</span>;
}

function SavingBadge({ value, guardrail }) {
  if (value === null || value === undefined) return <span className="ps-dash">—</span>;
  const blocked = guardrail === "blocked";
  return (
    <span className={`ps-badge ${blocked ? "red" : "green"}`}>
      {value.toFixed(1)}%{blocked ? " ⚠" : ""}
    </span>
  );
}

function WarningBanner({ tiers }) {
  const blocked = (tiers || []).filter((t) => t.isActive).flatMap((t) =>
    (t.prices || []).filter((p) => p.guardrailStatus === "blocked").map((p) => ({
      tier: t.displayName || t.templateId?.defaultDisplayName,
      carType: p.carType,
      saving: p.computedSavingPct,
    }))
  );
  if (!blocked.length) return null;
  return (
    <div className="ps-banner">
      <span className="ps-banner-icon">⚠️</span>
      <div>
        <b>{blocked.map((b) => `${b.tier} ${b.carType}`).join(", ")} breaches the 10% minimum saving guardrail.</b>{" "}
        Tier is blocked from publishing until the provider reprices or you approve an exception.
      </div>
    </div>
  );
}

function LocationChips({ locations }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  if (!locations?.length) {
    return (
      <div className="ps-locations">
        <div className="ps-locations-label">Locations using this set</div>
        <div className="ps-empty">No locations attached yet.</div>
      </div>
    );
  }
  return (
    <div className="ps-locations">
      <div className="ps-locations-label">Locations using this set ({locations.length})</div>
      <select
        className="ps-loc-select"
        value={selectedIdx}
        onChange={(e) => setSelectedIdx(Number(e.target.value))}
      >
        {locations.map((loc, i) => (
          <option key={loc.locationId || loc._id} value={i}>
            {loc.locationDisplayName || loc.locationId}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Connect Locations Modal ───────────────────────────────────────────────────
function ConnectLocationsModal({ set, onClose, onDone }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedLocId, setSelectedLocId] = useState("");

  useEffect(() => {
    const fetchLocs = async () => {
      try {
        const res = await axios.get(
          `${BASE}/package-sets/admin/seller-locations?sellerId=${set.sellerId}`,
          { headers: auth() }
        );
        const locs = res.data.data || res.data || [];
        setLocations(locs);
        if (locs.length) setSelectedLocId(locs[0].locationId);
      } catch {
        toast.error("Failed to load seller locations");
      } finally {
        setLoading(false);
      }
    };
    fetchLocs();
  }, [set.sellerId]);

  const attachedToThisSet = (loc) => loc.attachedSetId === String(set._id);
  const attachedToOther = (loc) => loc.attachedSetId && loc.attachedSetId !== String(set._id);

  const selectedLoc = locations.find((l) => l.locationId === selectedLocId) || null;

  const handleAttach = async (loc) => {
    setSaving(loc.locationId);
    try {
      await axios.post(
        `${BASE}/package-sets/admin/${set._id}/attach-location`,
        { locationId: loc.locationId, shopId: loc.shopId, sellerId: set.sellerId, locationDisplayName: loc.locationDisplayName },
        { headers: auth() }
      );
      setLocations((prev) =>
        prev.map((l) => l.locationId === loc.locationId ? { ...l, attachedSetId: String(set._id), attachedSetName: set.name } : l)
      );
      toast.success(`${loc.locationDisplayName} attached`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to attach");
    } finally {
      setSaving(null);
    }
  };

  const handleDetach = async (loc) => {
    setSaving(loc.locationId);
    try {
      await axios.delete(
        `${BASE}/package-sets/admin/${set._id}/detach-location/${loc.locationId}?sellerId=${set.sellerId}`,
        { headers: auth() }
      );
      setLocations((prev) =>
        prev.map((l) => l.locationId === loc.locationId ? { ...l, attachedSetId: null, attachedSetName: null } : l)
      );
      toast.success(`${loc.locationDisplayName} detached`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to detach");
    } finally {
      setSaving(null);
    }
  };

  const isAttachedHere = selectedLoc ? attachedToThisSet(selectedLoc) : false;
  const isAttachedElsewhere = selectedLoc ? attachedToOther(selectedLoc) : false;
  const isBusy = saving === selectedLocId;

  return (
    <div className="ps-modal-backdrop" onClick={onClose}>
      <div className="ps-modal" style={{ width: 480 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Connect Locations</h3>
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#888" }}>
              {set.name} · {set.sellerName || set.sellerId}
            </p>
          </div>
          <span onClick={onClose} style={{ fontSize: 18, color: "#bbb", cursor: "pointer" }}>✕</span>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#aaa", fontSize: 13 }}>Loading locations…</div>
        ) : locations.length === 0 ? (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#aaa", fontSize: 13 }}>This seller has no shop locations yet.</div>
        ) : (
          <>
            {/* Dropdown */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                Select location ({locations.length} total)
              </div>
              <select
                className="ps-loc-select"
                style={{ width: "100%", maxWidth: "100%" }}
                value={selectedLocId}
                onChange={(e) => setSelectedLocId(e.target.value)}
              >
                {locations.map((loc) => (
                  <option key={loc.locationId} value={loc.locationId}>
                    {loc.locationDisplayName || loc.city}
                    {attachedToThisSet(loc) ? " ✓ Attached" : ""}
                    {attachedToOther(loc) ? " · In another set" : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected location detail */}
            {selectedLoc && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                border: `1.5px solid ${isAttachedHere ? "#6b5fe4" : "#e8e8ec"}`,
                borderRadius: 10, padding: "12px 14px",
                background: isAttachedHere ? "#f0eeff" : "#fff",
                opacity: isAttachedElsewhere ? 0.65 : 1,
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1f3c" }}>
                    {selectedLoc.locationDisplayName || selectedLoc.city}
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 3 }}>
                    {selectedLoc.shopName}
                    {isAttachedHere && <span style={{ marginLeft: 6, color: "#6b5fe4", fontWeight: 600 }}>· Attached to this set</span>}
                    {isAttachedElsewhere && <span style={{ marginLeft: 6, color: "#e67e22", fontWeight: 600 }}>· In "{selectedLoc.attachedSetName}"</span>}
                  </div>
                </div>
                {isAttachedElsewhere ? (
                  <span style={{ fontSize: 11, color: "#aaa" }}>In another set</span>
                ) : isAttachedHere ? (
                  <button onClick={() => handleDetach(selectedLoc)} disabled={isBusy}
                    style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", background: "#fee2e2", color: "#991b1b", border: "1px solid #f0b8b8", borderRadius: 7, cursor: "pointer" }}>
                    {isBusy ? "…" : "Detach"}
                  </button>
                ) : (
                  <button onClick={() => handleAttach(selectedLoc)} disabled={isBusy}
                    style={{ fontSize: 11, fontWeight: 700, padding: "6px 14px", background: "#6b5fe4", color: "#fff", border: "none", borderRadius: 7, cursor: "pointer" }}>
                    {isBusy ? "…" : "Connect"}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={{ fontSize: 11, fontWeight: 600, padding: "7px 18px", border: "1px solid #ddd", borderRadius: 8, cursor: "pointer", background: "#fff" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Override Prices Modal ──────────────────────────────────────────────────────
function OverridePricesModal({ set, onClose, onDone }) {
  const activeTiers = (set.tiers || []).filter((t) => t.isActive);
  const [prices, setPrices] = useState(() => {
    const initial = {};
    activeTiers.forEach((tier) => {
      (set.carTypes || []).forEach((ct) => {
        const p = tier.prices?.find((x) => x.carType === ct);
        initial[`${tier._id}_${ct}`] = p?.price ?? "";
      });
    });
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const updatePrice = (tierId, carType, value) => {
    setPrices((prev) => ({ ...prev, [`${tierId}_${carType}`]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const overrides = [];
      activeTiers.forEach((tier) => {
        (set.carTypes || []).forEach((ct) => {
          const raw = prices[`${tier._id}_${ct}`];
          if (raw === "" || raw === undefined) return;
          overrides.push({ tierId: tier._id, carType: ct, price: Number(raw) });
        });
      });
      await axios.patch(
        `${BASE}/package-sets/admin/${set._id}/override-prices`,
        { overrides },
        { headers: auth() }
      );
      toast.success("Prices overridden");
      onDone();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to override prices");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ps-modal-backdrop" onClick={onClose}>
      <div className="ps-modal" onClick={(e) => e.stopPropagation()}>
        <h3>Override prices</h3>
        <p className="sub">{set.name} · {set.sellerName || set.sellerId}</p>

        {activeTiers.length === 0 ? (
          <div className="ps-empty">No active tiers to override.</div>
        ) : (
          activeTiers.map((tier) => (
            (set.carTypes || []).map((ct) => (
              <div className="ps-modal-row" key={`${tier._id}_${ct}`}>
                <label>{tier.displayName || tier.templateId?.defaultDisplayName} · {ct}</label>
                <input
                  type="number"
                  min="0"
                  value={prices[`${tier._id}_${ct}`]}
                  onChange={(e) => updatePrice(tier._id, ct, e.target.value)}
                />
              </div>
            ))
          ))
        )}

        <div className="ps-modal-actions">
          <button className="ps-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="ps-btn dark" onClick={handleSave} disabled={saving || activeTiers.length === 0}>
            {saving ? "Saving…" : "Save overrides"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Package Set Card ──────────────────────────────────────────────────────────
function PackageSetCard({ set, onConnectLocations, onOverridePrices }) {
  return (
    <div className="ps-card">
      <div className="ps-card-header">
        <div>
          <div className="ps-card-title-row">
            <h2 className="ps-card-title">{set.name}</h2>
            <StatusBadge status={set.isActive} />
          </div>
          <div className="ps-card-meta">
            {set.sellerName || "Unknown"} · <b>{set.locationCount} locations</b> · created{" "}
            {new Date(set.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
        <div className="ps-card-actions">
          <button className="ps-btn dark" onClick={() => onOverridePrices(set)}>
            Override prices
          </button>
          <button className="ps-btn violet" onClick={() => onConnectLocations(set)}>
            Connect locations
          </button>
        </div>
      </div>

      {/* Tiers table */}
      {set.tiers?.length > 0 && (
        <div className="ps-table-wrap">
          <table className="ps-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Days</th>
                {set.carTypes?.map((ct) => (
                  <>
                    <th key={ct + "_price"}>{ct}</th>
                    <th key={ct + "_pw"}>Per wash</th>
                    <th key={ct + "_sv"}>Saving</th>
                  </>
                ))}
                <th>Popular</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {set.tiers.filter((tier) => tier.isActive).map((tier) => (
                <tr key={tier._id} className={tier.prices?.some((p) => p.guardrailStatus === "blocked") ? "breach" : ""}>
                  <td className="ps-tier-name">{tier.displayName || tier.templateId?.defaultDisplayName}</td>
                  <td>{tier.templateId?.daysPerWeek}×/wk</td>
                  {set.carTypes?.map((ct) => {
                    const p = tier.prices?.find((x) => x.carType === ct);
                    return p ? (
                      <>
                        <td key={ct + "_p"}><span className="ps-price">AED {p.price}</span></td>
                        <td key={ct + "_pw"} className="ps-perwash">
                          {tier.templateId?.washCount ? (p.price / tier.templateId.washCount).toFixed(2) : "—"}
                        </td>
                        <td key={ct + "_sv"}>
                          <SavingBadge value={p.computedSavingPct} guardrail={p.guardrailStatus} />
                        </td>
                      </>
                    ) : (
                      <>
                        <td key={ct + "_p"}><span className="ps-price disabled">—</span></td>
                        <td key={ct + "_pw"}>—</td>
                        <td key={ct + "_sv"}>—</td>
                      </>
                    );
                  })}
                  <td>{tier.isFeatured ? <span className="ps-badge purple">⭐ Popular</span> : <span className="ps-dash">—</span>}</td>
                  <td><span className={`ps-badge ${tier.isActive ? "green" : "gray"}`}>{tier.isActive ? "Active" : "Off"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WarningBanner tiers={set.tiers} />
      <LocationChips locations={set.attachedLocations} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PackageSetsPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [query, setQuery] = useState("");
  const [connectModal, setConnectModal] = useState(null); // set object
  const [overrideModal, setOverrideModal] = useState(null); // set object

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${BASE}/package-sets/admin/all-providers`, { headers: auth() });
      const data = res.data.data || res.data;
      setProviders(data.providers || []);
      if (data.providers?.length && activeTab === "All") {
        // keep All as default
      }
    } catch {
      toast.error("Failed to load package sets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const providerNames = useMemo(() => ["All", ...providers.map((p) => p.shopName || p.sellerName)], [providers]);

  const allSets = useMemo(() => {
    return providers.flatMap((p) =>
      (p.sets || []).map((s) => ({ ...s, sellerName: p.sellerName }))
    );
  }, [providers]);

  const filteredSets = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allSets.filter((set) => {
      const matchTab = activeTab === "All" || set.sellerName === activeTab;
      const matchQ =
        !q ||
        set.name?.toLowerCase().includes(q) ||
        set.sellerName?.toLowerCase().includes(q) ||
        set.attachedLocations?.some((l) => l.locationDisplayName?.toLowerCase().includes(q));
      return matchTab && matchQ;
    });
  }, [allSets, activeTab, query]);

  return (
    <div className="ps-app">
      {/* Header */}
      <div className="ps-topbar">
        <div className="ps-header">
          <h1 className="sap-title">Package Sets — all providers</h1>
          <p className="sap-sub">
            Providers build sets, activate wash counts and set prices. You review, flag and override. A set is a price card; locations are attached to a set.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="ps-controls">
        <div className="ps-tabs">
          {providerNames.map((p) => (
            <button key={p} className={`ps-tab ${activeTab === p ? "active" : ""}`} onClick={() => setActiveTab(p)}>
              {p}
            </button>
          ))}
        </div>
        <div className="ps-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input placeholder="Search set or location..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 13 }}>Loading…</div>}

      {!loading && filteredSets.length === 0 && (
        <p className="ps-empty">No package sets found{query ? ` for "${query}"` : ""}.</p>
      )}

      {filteredSets.map((set) => (
        <PackageSetCard
          key={set._id}
          set={set}
          onConnectLocations={setConnectModal}
          onOverridePrices={setOverrideModal}
        />
      ))}

      {connectModal && (
        <ConnectLocationsModal
          set={connectModal}
          onClose={() => setConnectModal(null)}
          onDone={fetchAll}
        />
      )}

      {overrideModal && (
        <OverridePricesModal
          set={overrideModal}
          onClose={() => setOverrideModal(null)}
          onDone={fetchAll}
        />
      )}

      <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar />
    </div>
  );
}
